/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [P4-UX.2] HOW A TIME OF DAY IS SHOWN TO A HUMAN. ONE FUNCTION, BOTH SIDES.
 *
 *  The stored and transmitted form is a zero-padded 24-hour `HH:mm` and NOTHING here
 *  changes that: `slot.util.ts` compares those as STRINGS and the soundness of an
 *  overnight shift rests entirely on the padding — `8:00` would sort before `20:00`.
 *
 *  What changes is what an operator READS. Every duty roster in this building is written
 *  in 12-hour time, and `17:00` is a translation step nobody should be asked to perform.
 *
 *  ⚠️ IT LIVES IN `shared` BECAUSE BOTH SIDES FORMAT. The API denormalises a `slot_label`
 *  onto every duty row at publish time, and the web renders shift times, coverage tooltips
 *  and the field the operator types into. Two implementations of "5:00 PM" would drift the
 *  day somebody fixed one of them.
 *
 *  ⚠️ IT IS NOT `luxon`. A time of day here is a `HH:mm` STRING with no date and no zone,
 *  and constructing a DateTime to print it would invite exactly the instant/business-time
 *  confusion CLAUDE.md §7 spends a paragraph forbidding.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */

import {
  NO_FIXED_TIME_LABEL,
  WHOLE_DAY_LABEL,
  standDownRollsOver,
  wholeDayWindowLabel,
} from '../constants/slotLabel.constant';

/** `HH:mm`, 00:00–23:59, plus `24:00` — which a coverage bar's right-hand edge uses. */
const HH_MM = /^([01]\d|2[0-4]):([0-5]\d)$/;

/**
 * `17:00` → `5:00 PM`. `00:00` → `12:00 AM`. `12:00` → `12:00 PM`.
 *
 * ⚠️ NOON AND MIDNIGHT ARE THE TWO A NAIVE `hour % 12` GETS WRONG — both would print as
 * `0:00`. They are the reason this is a function rather than a template literal at each
 * call site, and both are pinned in the test.
 *
 * Anything that is not a well-formed `HH:mm` is returned UNCHANGED rather than mangled: a
 * caller rendering `''` for an unset time must keep getting `''`, not `12:00 AM`.
 */
export const toTwelveHour = (value: string): string => {
  const match = HH_MM.exec(value.trim());
  if (!match) return value;

  const hour = Number(match[1]);
  const minute = match[2] ?? '00';
  // 24:00 is the end of the day, which reads as midnight rather than as hour twenty-four.
  const normalised = hour === 24 ? 0 : hour;
  const suffix = normalised < 12 ? 'AM' : 'PM';
  const twelve = normalised % 12 === 0 ? 12 : normalised % 12;

  return `${String(twelve)}:${minute} ${suffix}`;
};

/** Just the half of the day, for a caption sitting beside a value that already shows the digits. */
export const toMeridiem = (value: string): string => {
  const match = HH_MM.exec(value.trim());
  if (!match) return '';
  return Number(match[1]) % 24 < 12 ? 'AM' : 'PM';
};

/**
 * Rewrites every `HH:mm` inside a longer string.
 *
 * ⚠️ THIS EXISTS FOR ONE REASON: `slot_label` IS DENORMALISED ONTO EVERY DUTY ROW AT
 * PUBLISH TIME. Rows written before this change carry `Mon 5 Jan · 08:00–20:00`, and a
 * label is a stored string rather than something the client rebuilds — so without this,
 * changing the generator would leave every existing duty reading in 24-hour time for ever
 * while new ones read in 12-hour. Applied at the render site, it makes both agree.
 *
 * The date part (`ccc d LLL`) contains no `HH:mm`, so the match cannot touch it.
 */
export const humaniseTimes = (text: string): string =>
  /*
   * ⚠️ [C5b review] `(?!\s*[AP]M\b)` — A TIME THAT ALREADY CARRIES ITS MERIDIEM IS NOT REWRITTEN.
   * `generateSlots` has stored 12-hour labels since [P4-UX.2], and this matched their two-digit
   * hours too: `5:00 PM–11:59 PM` rendered `5:00 PM–11:59 AM PM` on the order sheet, the officer's
   * notification, the fill sheet and the builder, and `From 12:00 PM` became `From 12:00 PM PM`
   * (the "ordering trap" below was this defect seen from one side). MEASURED on live `pamsDev`:
   * 20 of 78 duties carry such a label. A legacy 24-hour label has no meridiem to see, so it is
   * rewritten exactly as before.
   */
  text.replace(/\b([01]\d|2[0-4]):([0-5]\d)\b(?!\s*[AP]M\b)/g, (match) => toTwelveHour(match));

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [C3a §6] `Sat 15 Aug · No fixed time` -> `Sat 15 Aug · From 3:00 PM`, AT THE RENDER SITE.
 *
 *  THIS IS `humaniseTimes`' ARGUMENT APPLIED A SECOND TIME, and the reason it is a rewrite rather
 *  than a change to the producer is the same one: `slot_label` is DENORMALISED onto every duty row
 *  at publish, so teaching `generateSlots` to say this would leave every already-issued order
 *  reading `No fixed time` for ever — which is a migration, and §6 is a LABELLING rule.
 *
 *  ⚠️ `generateSlots` COULD NOT DO IT ANYWAY. It receives a `Schedule` and nothing else; the
 *  reporting time lives on the duty POINT, one level up. Widening its signature moves four call
 *  sites, two of which are the builder's live client-side preview, where no reporting time is in
 *  scope at all.
 *
 *  ⚠️ IT IS TOTAL, WHICH IS WHAT MAKES IT SAFE TO COMPOSE. A label with no `No fixed time` in it
 *  comes back UNCHANGED, so a shift label cannot be mangled by a caller that forgot to check
 *  `hasFixedTime` — the gate is belt-and-braces rather than the only defence.
 *
 *  ⚠️ ⚠️ THE ORDER IS NOT FREE: APPLY THIS **AFTER** `humaniseTimes`, NEVER BEFORE. MEASURED.
 *  The obvious claim is that the two commute — `humaniseTimes` needs a two-digit hour and this
 *  writes a one-digit one — and it is FALSE at noon and midnight, the two `toTwelveHour` already
 *  exists for. `humaniseTimes('From 12:00 PM')` matches `12:00`, rewrites it, and yields
 *  `From 12:00 PM PM`. Every render site therefore reads
 *  `withReportingTime(humaniseTimes(label), time)`, and `timeFormat.util.test.ts` pins the
 *  wrong order producing the doubled meridiem so nobody re-derives the false version of this.
 *  ⚠️ [amended C5b review] THE TRAP WAS A DEFECT IN `humaniseTimes`, AND IT IS FIXED THERE: a time
 *  already carrying its meridiem is not rewritten, so the two now commute. The same defect had been
 *  doubling the meridiem on every stored 12-hour label with a ten-to-twelve o'clock time, which no
 *  ordering could avoid. `humaniseTimes` first is still how every render site reads.
 *
 *  ⚠️ [amended STAND-DOWN] RENAMED FROM `withReportingTime`, AND IT TAKES RAW `HH:mm` NOW — BOTH
 *  TIMES. A whole-day duty may carry a stand-down time as well, and whether that lands the next
 *  morning is a comparison of the two UNFORMATTED strings, so the formatting moved inside. The
 *  rename is deliberate: a caller still passing one pre-formatted time is a compile error rather
 *  than a label that silently reads `From 3:00 PM PM`. Duty Mitra never imported this (its
 *  eslint config bans `utils/`); it builds the same sentence from `wholeDayWindowLabel`.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */
const presentTime = (value: string | null | undefined): string | null =>
  value === null || value === undefined || value.trim().length === 0 ? null : value.trim();

/**
 * A whole-day duty's time fragment — `From 3:00 PM`, `3:00 PM–9:00 PM`, `Until 9:00 PM` — or `null`.
 *
 * ⚠️ BLANK IS ABSENT. `TimeField` emits `''` when cleared, and `toTwelveHour('')` would render an
 * empty `From `.
 */
export const wholeDayTimes = (
  reportingTime: string | null | undefined,
  standDownTime: string | null | undefined,
): string | null => {
  const from = presentTime(reportingTime);
  const until = presentTime(standDownTime);
  return wholeDayWindowLabel(
    from === null ? null : toTwelveHour(from),
    until === null ? null : toTwelveHour(until),
    until !== null && standDownRollsOver(from, until),
  );
};

export const withWholeDayTimes = (
  label: string,
  reportingTime: string | null | undefined,
  standDownTime: string | null | undefined,
): string => {
  const times = wholeDayTimes(reportingTime, standDownTime);
  return times === null ? label : label.replace(NO_FIXED_TIME_LABEL, times);
};

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [C5 §4a] A STORED SLOT LABEL, RENDERED — AND `No fixed time` NEVER SURVIVES IT.
 *
 *  Four call sites wrote this out identically — the printed sheet, the fill sheet's copy, the
 *  arrangement detail page and `dashboardFill.service.ts` — each as
 *  `withWholeDayTimes(humaniseTimes(label), hasFixedTime ? null : from, hasFixedTime ? null :
 *  until)`. Four copies of one expression, each carrying the same ordering trap in a comment.
 *
 *  ⚠️ `humaniseTimes` FIRST, ALWAYS. The other order DOUBLED THE MERIDIEM at noon —
 *  `humaniseTimes('From 12:00 PM')` yielded `From 12:00 PM PM`. That was a comment repeated at
 *  every call site and is now a property of the one function, which is the only place it cannot
 *  be got wrong. ⚠️ [amended C5b review] `humaniseTimes` no longer rewrites a time that carries its
 *  meridiem, so that trap is closed at its cause — and with it `5:00 PM–11:59 AM PM`.
 *
 *  ⚠️ AND THE LAST SUBSTITUTION IS §4a ITSELF. `withWholeDayTimes` leaves the label untouched
 *  when there are no times to put in, so a whole-day duty with no reporting time still read
 *  `Sat 15 Aug · No fixed time`. It reads `Sat 15 Aug · Whole day` now — the string the API's
 *  own `slotTimeLabel` already emits for exactly this case, so the product converges on one
 *  phrasing rather than four.
 *
 *  ⚠️ THE STORED LABEL IS NOT REWRITTEN. `deployments.slot_label` keeps `NO_FIXED_TIME_LABEL`:
 *  changing it is a MIGRATION over every duty ever stamped, and [C3a §6] settled that this is a
 *  LABELLING rule applied at the render site. This function IS that site, for every caller that
 *  can import it.
 *
 *  ⚠️ `pwa` CANNOT CALL IT — `@pams/shared/utils` drags zod and luxon into a 150 KB budget, and
 *  `pwa/eslint.config.js` bans the import. It composes the same three shapes from the duty's own
 *  fields in `pwa/src/lib/dutyTime.ts`, whose header names this one.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const displaySlotLabel = (duty: {
  slotLabel: string;
  hasFixedTime: boolean;
  /** `HH:mm`, or `null`. A caller holding an INSTANT converts with `istTimeOfDay` first. */
  reportingTime: string | null | undefined;
  standDownTime: string | null | undefined;
}): string =>
  withWholeDayTimes(
    humaniseTimes(duty.slotLabel),
    duty.hasFixedTime ? null : duty.reportingTime,
    duty.hasFixedTime ? null : duty.standDownTime,
  ).replace(NO_FIXED_TIME_LABEL, WHOLE_DAY_LABEL);
